import { hasAccess } from "@spiriidk/auth";
import type { Permission, ConnectMenuItem } from "@spiriidk/connect-configs";
import { useMemo } from "react";

interface UseFilteredMenuItemsParams<T extends ConnectMenuItem> {
  menuItems: T[];
  userPermissions: Permission[];
  isBetaEnabled?: boolean;
  isSpiriiAdminUser?: boolean;
}

function useFilteredMenuItems<T extends ConnectMenuItem>({
  menuItems,
  userPermissions,
  isBetaEnabled = false,
  isSpiriiAdminUser = false,
}: UseFilteredMenuItemsParams<T>): T[] {
  return useMemo(() => {
    return menuItems.filter((item) => {
      // Check permissions and roles using hasAccess utility
      if (item.permissions?.length || item.roles?.length) {
        if (
          !hasAccess({
            requiredPermissions: item.permissions,
            requiredRoles: item.roles,
            userPermissions,
          })
        ) {
          return false;
        }
      }

      // Check spiriiAdminOnly flag
      if (item.spiriiAdminOnly && !isSpiriiAdminUser) {
        return false;
      }

      // Check beta flag
      return !(item.beta && !isBetaEnabled);
    });
  }, [menuItems, userPermissions, isBetaEnabled, isSpiriiAdminUser]);
}

export { useFilteredMenuItems };
